import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { MetaSEO } from "@/components/common/MetaSEO";
import { useTranslation } from "react-i18next";

const fadeUp = {
  hidden: { opacity: 0, y: 32 },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.7, delay: i * 0.12, ease: [0.25, 0.46, 0.45, 0.94] },
  }),
};

const Pledge = () => {
  const { t } = useTranslation();

  const commitments = [
    {
      number: "01",
      titleKey: "pledge.c1_title",
      paragraphKeys: ["pledge.c1_p1", "pledge.c1_p2", "pledge.c1_p3", "pledge.c1_p4"],
    },
    {
      number: "02",
      titleKey: "pledge.c2_title",
      paragraphKeys: ["pledge.c2_p1", "pledge.c2_p2", "pledge.c2_p3", "pledge.c2_p4"],
    },
    {
      number: "03",
      titleKey: "pledge.c3_title",
      paragraphKeys: ["pledge.c3_p1", "pledge.c3_p2", "pledge.c3_p3", "pledge.c3_p4"],
    },
    {
      number: "04",
      titleKey: "pledge.c4_title",
      paragraphKeys: ["pledge.c4_p1", "pledge.c4_p2", "pledge.c4_p3"],
    },
    {
      number: "05",
      titleKey: "pledge.c5_title",
      paragraphKeys: ["pledge.c5_p1", "pledge.c5_p2", "pledge.c5_p3"],
    },
  ];

  return (
    <>
      <MetaSEO
        title={t("pledge.seo_title")}
        description={t("pledge.seo_description")}
        canonical="https://www.babete.pt/pledge"
        ogType="article"
        schema={{
          "@context": "https://schema.org",
          "@type": "Article",
          headline: t("pledge.seo_title"),
          description: t("pledge.seo_description"),
          image: "https://www.babete.pt/og-image.png",
          url: "https://www.babete.pt/pledge",
          datePublished: "2025-01-15",
          dateModified: "2026-03-16",
          author: {
            "@type": "Organization",
            name: "Babete",
          },
          publisher: {
            "@type": "Organization",
            name: "Babete",
            logo: {
              "@type": "ImageObject",
              url: "https://www.babete.pt/logo.webp",
            },
          },
          mainEntityOfPage: {
            "@type": "WebPage",
            "@id": "https://www.babete.pt/pledge",
          },
        }}
      />

      <style>{`
        html {
          scroll-snap-type: y mandatory;
          scroll-padding-top: 20px;
        }
      `}</style>

      <main className="min-h-screen bg-white text-neutral-900 antialiased">
        {/* Hero */}
        <section
          id="hero"
          className="relative flex min-h-[70vh] snap-start snap-always flex-col items-center justify-center px-6 py-32 text-center"
        >
          <motion.p
            initial="hidden"
            animate="visible"
            custom={0}
            variants={fadeUp}
            className="mb-6 text-xs font-medium uppercase tracking-[0.25em] text-neutral-400"
          >
            {t("pledge.hero_label")}
          </motion.p>
          <motion.h1
            initial="hidden"
            animate="visible"
            custom={1}
            variants={fadeUp}
            className="mx-auto max-w-3xl text-4xl font-semibold leading-tight tracking-tight sm:text-5xl md:text-6xl"
          >
            {t("pledge.hero_title")}
          </motion.h1>
          <motion.p
            initial="hidden"
            animate="visible"
            custom={2}
            variants={fadeUp}
            className="mx-auto mt-8 max-w-2xl text-lg leading-relaxed text-neutral-500 sm:text-xl"
          >
            {t("pledge.hero_subtitle_1")} <br />{t("pledge.hero_subtitle_2")}
          </motion.p>

          {/* Subtle divider line */}
          <motion.div
            initial={{ scaleX: 0 }}
            animate={{ scaleX: 1 }}
            transition={{ duration: 1.2, delay: 0.6, ease: [0.25, 0.46, 0.45, 0.94] }}
            className="mt-20 h-px w-16 bg-neutral-200"
          />
        </section>

        {/* Principles Intro */}
        <section id="principles" className="px-6 py-24 snap-start snap-always">
          <div className="mx-auto max-w-2xl">
            <motion.h2
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-80px" }}
              custom={0}
              variants={fadeUp}
              className="text-2xl font-semibold tracking-tight sm:text-3xl"
            >
              {t("pledge.principles_title")}
            </motion.h2>
            <motion.p
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-80px" }}
              custom={1}
              variants={fadeUp}
              className="mt-6 text-base leading-relaxed text-neutral-500 sm:text-lg"
            >
              {t("pledge.principles_intro_1")}
              <br /> <br /> {t("pledge.principles_intro_2")}
            </motion.p>
          </div>
        </section>

        <article>
          {/* Commitments */}
          {commitments.map((commitment, idx) => {
            const title = t(commitment.titleKey);
            const sectionId = title.toLowerCase().replace(/\s+/g, '-').replace(/[^\w-]/g, '');
            return (
              <section
                key={commitment.number}
                id={sectionId}
                className={`px-6 py-24 snap-start snap-always ${idx % 2 === 1 ? "bg-neutral-50" : "bg-white"}`}
              >
                <div className="mx-auto max-w-2xl">
                  <motion.span
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true, margin: "-80px" }}
                    custom={0}
                    variants={fadeUp}
                    className="mb-4 block font-mono text-sm text-neutral-300"
                  >
                    {commitment.number}
                  </motion.span>
                  <motion.h3
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true, margin: "-80px" }}
                    custom={0.5}
                    variants={fadeUp}
                    className="text-xl font-semibold tracking-tight sm:text-2xl"
                  >
                    {title}
                  </motion.h3>
                  <div className="mt-8 space-y-5">
                    {commitment.paragraphKeys.map((pKey, pIdx) => (
                      <motion.p
                        key={pIdx}
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true, margin: "-60px" }}
                        custom={1 + pIdx * 0.3}
                        variants={fadeUp}
                        className="whitespace-pre-line text-base leading-relaxed text-neutral-500"
                      >
                        {t(pKey)}
                      </motion.p>
                    ))}
                  </div>
                </div>
              </section>
            );
          })}
        </article>

        {/* Closing */}
        <section id="closing" className="px-6 py-32 snap-start snap-always">
          <div className="mx-auto max-w-2xl text-center">
            <motion.h2
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-80px" }}
              custom={0}
              variants={fadeUp}
              className="text-2xl font-semibold tracking-tight sm:text-3xl"
            >
              {t("pledge.closing_title")}
            </motion.h2>
            <motion.p
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-80px" }}
              custom={1}
              variants={fadeUp}
              className="mx-auto mt-6 max-w-xl text-base leading-relaxed text-neutral-500 sm:text-lg"
            >
              {t("pledge.closing_p1")}
            </motion.p>
            <motion.p
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-80px" }}
              custom={1.5}
              variants={fadeUp}
              className="mx-auto mt-4 max-w-xl text-base leading-relaxed text-neutral-500 sm:text-lg"
            >
              {t("pledge.closing_p2")}
            </motion.p>
          </div>
        </section>

        {/* Footer CTA */}
        <section className="border-t border-neutral-100 bg-neutral-50 px-6 py-20 snap-start snap-always">
          <div className="mx-auto max-w-2xl text-center">
            <motion.p
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-60px" }}
              custom={0}
              variants={fadeUp}
              className="text-sm font-medium uppercase tracking-[0.2em] text-neutral-400"
            >
              {t("pledge.cta_label")}
            </motion.p>
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-60px" }}
              custom={1}
              variants={fadeUp}
              className="mt-10 flex flex-col items-center gap-4 sm:flex-row sm:justify-center"
            >
              <a
                href="https://urbanismo.babete.pt"
                target="_blank"
                rel="noopener noreferrer"
                className="group inline-flex items-center justify-center gap-2 rounded-full border border-neutral-200 bg-white px-6 py-3 text-sm font-medium text-neutral-900 transition-all hover:border-neutral-900 hover:shadow-sm w-full sm:w-auto min-h-[44px]"
              >
                {t("pledge.cta_urbanismo")}
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
              </a>
              <a
                href="https://condominio.babete.pt"
                target="_blank"
                rel="noopener noreferrer"
                className="group inline-flex items-center justify-center gap-2 rounded-full border border-neutral-200 bg-white px-6 py-3 text-sm font-medium text-neutral-900 transition-all hover:border-neutral-900 hover:shadow-sm w-full sm:w-auto min-h-[44px]"
              >
                {t("pledge.cta_condominios")}
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
              </a>
            </motion.div>
          </div>
        </section>
      </main>
    </>
  );
};

export default Pledge;
